/**
 * RateFormDrawer - Slide-out drawer for creating/editing energy rate profiles
 */

import { useState, useEffect, useMemo } from 'react';
import { AdminFormDrawer } from './AdminFormDrawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { UtilityProviderNameCombobox } from './UtilityProviderCombobox';
import type { EnergyRate, RateFormData } from '@/types/rates';
import {
  COUNTRIES,
  getRegionsForCountry,
  getRegionName,
  type CountryCode,
} from '@/types/utilityProviders';

const NEM_OPTIONS = ['NEM 1.0', 'NEM 2.0', 'NEM 3.0'];

interface RateFormDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: RateFormData) => Promise<void>;
  rate?: EnergyRate | null;
  isSubmitting?: boolean;
}

export function RateFormDrawer({
  open,
  onClose,
  onSubmit,
  rate,
  isSubmitting = false,
}: RateFormDrawerProps) {
  const isEditing = !!rate;

  // Form state
  const [formData, setFormData] = useState<RateFormData>({
    name: '',
    description: '',
    gridSellRate: 0.08,
    gridImportRate: 0.18,
    dieselCostPerGallon: 4.50,
    utilityProvider: '',
    nemVersion: '',
    countryCode: 'US',
    stateProvince: '',
    isLocked: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Get available regions based on selected country
  const availableRegions = useMemo(() => {
    const country = (formData.countryCode || 'US') as CountryCode;
    return getRegionsForCountry(country);
  }, [formData.countryCode]);

  // Initialize form when rate changes
  useEffect(() => {
    if (rate) {
      setFormData({
        name: rate.name,
        description: rate.description || '',
        gridSellRate: rate.gridSellRate,
        gridImportRate: rate.gridImportRate,
        dieselCostPerGallon: rate.dieselCostPerGallon,
        utilityProvider: rate.utilityProvider || '',
        nemVersion: rate.nemVersion || '',
        countryCode: rate.countryCode || 'US',
        stateProvince: rate.stateProvince || '',
        isLocked: rate.isLocked,
      });
    } else {
      // Reset to defaults for new rate
      setFormData({
        name: '',
        description: '',
        gridSellRate: 0.08,
        gridImportRate: 0.18,
        dieselCostPerGallon: 4.50,
        utilityProvider: '',
        nemVersion: '',
        countryCode: 'US',
        stateProvince: '',
        isLocked: false,
      });
    }
    setErrors({});
  }, [rate, open]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (formData.gridSellRate < 0) {
      newErrors.gridSellRate = 'Must be non-negative';
    }

    if (formData.gridImportRate < 0) {
      newErrors.gridImportRate = 'Must be non-negative';
    }

    if (formData.dieselCostPerGallon < 0) {
      newErrors.dieselCostPerGallon = 'Must be non-negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    await onSubmit(formData);
  };

  const updateField = <K extends keyof RateFormData>(field: K, value: RateFormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  return (
    <AdminFormDrawer
      open={open}
      onClose={onClose}
      title={isEditing ? 'Edit Rate Profile' : 'Create Rate Profile'}
      description={isEditing
        ? 'Update energy rate configuration'
        : 'Create a new energy rate profile for assignment to companies, groups, or units'
      }
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name */}
        <div className="space-y-2">
          <Label htmlFor="name">Name *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={e => updateField('name', e.target.value)}
            placeholder="e.g., California NEM 3.0"
            className={errors.name ? 'border-destructive' : ''}
          />
          {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
        </div>

        {/* Description */}
        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={e => updateField('description', e.target.value)}
            placeholder="Brief description of this rate profile"
            rows={2}
          />
        </div>

        {/* Rate Values Section */}
        <div className="border border-border rounded-lg p-4 space-y-4">
          <h3 className="font-semibold text-sm text-foreground">Rate Values</h3>

          <div className="grid grid-cols-2 gap-4">
            {/* Grid Import Rate */}
            <div className="space-y-2">
              <Label htmlFor="gridImportRate">Grid Import ($/kWh) *</Label>
              <Input
                id="gridImportRate"
                type="number"
                step="0.01"
                min="0"
                value={formData.gridImportRate}
                onChange={e => updateField('gridImportRate', parseFloat(e.target.value) || 0)}
                className={errors.gridImportRate ? 'border-destructive' : ''}
              />
              <p className="text-xs text-muted-foreground">Cost to buy from grid</p>
              {errors.gridImportRate && <p className="text-sm text-destructive">{errors.gridImportRate}</p>}
            </div>

            {/* Grid Sell Rate */}
            <div className="space-y-2">
              <Label htmlFor="gridSellRate">Grid Sell ($/kWh) *</Label>
              <Input
                id="gridSellRate"
                type="number"
                step="0.01"
                min="0"
                value={formData.gridSellRate}
                onChange={e => updateField('gridSellRate', parseFloat(e.target.value) || 0)}
                className={errors.gridSellRate ? 'border-destructive' : ''}
              />
              <p className="text-xs text-muted-foreground">Revenue from selling to grid</p>
              {errors.gridSellRate && <p className="text-sm text-destructive">{errors.gridSellRate}</p>}
            </div>
          </div>

          {/* Diesel Cost */}
          <div className="space-y-2">
            <Label htmlFor="dieselCostPerGallon">Diesel Cost ($/gallon)</Label>
            <Input
              id="dieselCostPerGallon"
              type="number"
              step="0.01"
              min="0"
              value={formData.dieselCostPerGallon}
              onChange={e => updateField('dieselCostPerGallon', parseFloat(e.target.value) || 0)}
              className={errors.dieselCostPerGallon ? 'border-destructive' : ''}
            />
            {errors.dieselCostPerGallon && <p className="text-sm text-destructive">{errors.dieselCostPerGallon}</p>}
          </div>
        </div>

        {/* Utility Metadata Section */}
        <div className="border border-border rounded-lg p-4 space-y-4">
          <h3 className="font-semibold text-sm text-foreground">Utility Information (Optional)</h3>

          <div className="grid grid-cols-2 gap-4">
            {/* Country */}
            <div className="space-y-2">
              <Label htmlFor="countryCode">Country</Label>
              <Select
                value={formData.countryCode || 'US'}
                onValueChange={v => {
                  updateField('countryCode', v);
                  // Clear state/province when country changes
                  updateField('stateProvince', '');
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(COUNTRIES).map(([code, name]) => (
                    <SelectItem key={code} value={code}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* State/Province/Region */}
            <div className="space-y-2">
              <Label htmlFor="stateProvince">
                {formData.countryCode === 'UK' ? 'Region' : formData.countryCode === 'CA' ? 'Province' : 'State'}
              </Label>
              <Select
                value={formData.stateProvince || 'none'}
                onValueChange={v => updateField('stateProvince', v === 'none' ? '' : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={`Select ${formData.countryCode === 'UK' ? 'region' : formData.countryCode === 'CA' ? 'province' : 'state'}`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {availableRegions.map(region => (
                    <SelectItem key={region} value={region}>
                      {getRegionName((formData.countryCode || 'US') as CountryCode, region)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Utility Provider */}
          <div className="space-y-2">
            <Label htmlFor="utilityProvider">Utility Provider</Label>
            <UtilityProviderNameCombobox
              value={formData.utilityProvider}
              onValueChange={v => updateField('utilityProvider', v)}
              countryCode={(formData.countryCode || 'US') as CountryCode}
              placeholder="Search or add provider..."
              allowCreate={true}
            />
            <p className="text-xs text-muted-foreground">
              Search existing providers or type to add new
            </p>
          </div>

          {/* NEM Version */}
          <div className="space-y-2">
            <Label htmlFor="nemVersion">NEM Version</Label>
            <Select
              value={formData.nemVersion || 'none'}
              onValueChange={v => updateField('nemVersion', v === 'none' ? '' : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select version" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {NEM_OPTIONS.map(nem => (
                  <SelectItem key={nem} value={nem}>{nem}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Lock Toggle */}
        <div className="flex items-center justify-between border border-border rounded-lg p-4">
          <div>
            <Label htmlFor="isLocked" className="text-sm font-medium">Lock Rate Profile</Label>
            <p className="text-xs text-muted-foreground mt-1">
              Locked profiles cannot be edited or deleted
            </p>
          </div>
          <Switch
            id="isLocked"
            checked={formData.isLocked}
            onCheckedChange={checked => updateField('isLocked', checked)}
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="flex-1"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="flex-1 bg-[#8FB560] hover:bg-[#7a9d4e]"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isEditing ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              isEditing ? 'Update Rate' : 'Create Rate'
            )}
          </Button>
        </div>
      </form>
    </AdminFormDrawer>
  );
}
